import { createStore } from "vuex";
import Moment from "moment";

import Api from "@/modules/axios";
import i18n from "@/modules/i18n";
import Sentry from "@/modules/sentry";
import { useToast } from "@/utils/toast";
import config from "@/config";
import { isOnPublicDomain } from "@/modules/utils";
import { PAYS_AVEC_CATALOGUE } from "@/modules/cataloguePublic";
import Panier from "@/store/panier";
import Organisme from "@/store/organisme";
import Dotation from "@/store/dotation";
import TableClassic from "@/store/tableclassic";
import TacheRepository from "@/domains/taches/tache.repository";

let inFlightLoginPromise = null;
// Resélection de l'organisme après reconnexion : le service de droits appelé par /switch/ peut être lent ou en panne
const nbEssaisReselection = 3;
// Les coupures observées durent plusieurs secondes : des essais rapprochés échoueraient tous
const delaiEntreEssaisMs = 3000;

/**
 * Resélectionne l'organisme d'avant l'expiration, pour un utilisateur multi-organisme revenu sans organisme choisi.
 * Un refus du serveur (droits retirés entre-temps…) est définitif ; une panne est retentée un nombre limité de fois.
 * @param {Object} identite Identité vue à l'expiration (state.identiteExpiree).
 * @param {Number} essai Numéro de la tentative en cours.
 * @returns {Promise<String>} "ok", "refus" (contexte non repris) ou "echec" (panne, tentatives épuisées).
 */
function reselectionnerOrganisme(identite, essai = 1) {
  return Api()
    // Un refus mène au rechargement de la page : la page d'erreur 403 n'a pas lieu d'être
    .post(
      "/switch/",
      { organisme: identite.pkOrganisme, chosen_org: true },
      { sansMiseEnAttente: true, sansPageErreur: true },
    )
    .then(({ data }) => (data?.id_organisme === identite.idOrganisme ? "ok" : "refus"))
    .catch((err) => {
      const status = err.response?.status;
      if (status && status < 500) {
        return "refus";
      }
      if (essai >= nbEssaisReselection) {
        return "echec";
      }
      return new Promise((resolve) => { setTimeout(resolve, delaiEntreEssaisMs); })
        .then(() => reselectionnerOrganisme(identite, essai + 1));
    });
}

export default createStore({
  modules: {
    Panier,
    Organisme,
    Dotation,
    TableClassic,
  },
  state: {
    user: {
      id: null,
      contact: {
        id: null,
        prenom: null,
        nom: null,
        fonction_4d: null,
        email: null,
        tel: null,
        genre: null,
      },
      username: null,
      email: null,
      is_staff: false,
      avatar: `${config.api.baseUrl}/media/uploads/avatars/bmp_24.png`,
      avatar_from_library: null,
      role: "Visiteur",
      cookies: (() => {
        try {
          return JSON.parse(localStorage.getItem("cookies_consent")) || null;
        } catch (e) {
          console.error(e)
          return null;
        }
      })(),
    },
    permissions: [],
    devisCount: null,
    devisSoumisApprouvesCount: null,
    accessForbidden: false,
    peutIgnorerBlocage: false,
    askLinkAccount: false,
    errorStatus: null,
    isLogged: false,
    isInitialCheckDone: false,
    isRedirecting: false,
    preferences: JSON.parse(localStorage.getItem("preferences")) || {},
    suggestionFilter: null,
    isSuggestionFilterOpen: true,
    askChooseOrg: false,
    componentSearchKey: 0,
    advancedSearchActive: false,
    selectedLines: [], // Offres à rajouter dans la liste (lors de la modale de sélection/création)
    activeLists: [],
    isFetchingActiveLists: false,
    panierToListResolver: null,
    identifyFailed: false,
    sessionConflict: false,
    /**
     * Session expirée alors que le front se croyait connecté (401 reçue).
     * identiteExpiree garde qui était connecté pour n'accepter la reprise que par le même compte.
     */
    sessionExpiree: false,
    identiteExpiree: null,
    /**
     * Échéance de la session (cookie HttpOnly, illisible par le front) : expireLe en ms, horloge du serveur,
     * et decalage = heure serveur - heure client, pour ne pas dépendre de l'horloge du poste.
     */
    echeanceSession: null,
    /**
     * Fin de session proche : la modale de session s'affiche en avertissement,
     * sauf si l'utilisateur l'a repoussée (« Plus tard ») pour cette échéance-là.
     */
    finSessionProche: false,
    avertissementIgnorePour: null,
    taches: [],
    /**
     * Devis sur lequel on veut ajouter une référence (vue limitée du site)
     */
    devisAjoutReference: null,
    referencesAjouteesAuDevis: [],
    modificationsDevis: [],
    isMaitreComptaRegion: false,
    cataloguePublic: null,
    cataloguePublicActif: false,
    /**
     * Pays choisi par le visiteur à son arrivée sur le catalogue public (france, suisse, belgique ou autre).
     */
    cataloguePublicPays: null,
  },
  getters: {
    isLogged(state) {
      return state.isLogged;
    },
    isInitialCheckDone(state) {
      return state.isInitialCheckDone;
    },
    isRedirecting(state) {
      return state.isRedirecting;
    },
    isOnPublicDomain() {
      return isOnPublicDomain();
    },
    cataloguePublicSlug(state) {
      return state.cataloguePublic?.slug || null;
    },
    isCataloguePublicActif(state) {
      return state.cataloguePublicActif || state.cataloguePublic !== null;
    },
    cataloguePublicPays(state) {
      return state.cataloguePublicPays;
    },
    /**
     * Le visiteur doit choisir son pays (ou n'a pas de catalogue pour le pays choisi)
     * avant d'accéder au catalogue public.
     */
    isChoixPaysCataloguePublicRequis(state, getters) {
      return !state.isLogged
        && getters.isCataloguePublicActif
        && !PAYS_AVEC_CATALOGUE.includes(state.cataloguePublicPays);
    },
    isOperateur(state) {
      return state.user?.role === "Opérateur LDE";
    },
    /**
     * Catalogues actifs à utiliser dans le menu et la navigation.
     * Pour les utilisateurs non connectés, on construit un catalogue synthétique
     * à partir du slug du catalogue public.
     */
    activeCataloguesForMenu(state, getters) {
      const fromOrganisme = getters.organismeActuel?.catalogues_actifs;
      if (fromOrganisme?.length) {
        return fromOrganisme;
      }
      if (state.cataloguePublic) {
        return [{ nom: state.cataloguePublic.libelle_client, slug: state.cataloguePublic.slug, type: "numerique" }];
      }
      return [];
    },
    user(state) {
      return state.user;
    },
    devisCount(state) {
      return state.devisCount;
    },
    devisSoumisApprouvesCount(state) {
      return state.devisSoumisApprouvesCount;
    },
    accessForbidden(state) {
      return state.accessForbidden;
    },
    peutIgnorerBlocage(state) {
      return state.peutIgnorerBlocage;
    },
    askLinkAccount(state) {
      return state.askLinkAccount;
    },
    errorStatus(state) {
      return state.errorStatus;
    },
    preferences(state) {
      return state.preferences;
    },
    suggestionFilter(state) {
      return state.suggestionFilter;
    },
    isSuggestionFilterOpen(state) {
      return state.isSuggestionFilterOpen;
    },
    hasPerm: (state) => (perm) => state.permissions.includes(perm),
    hasPerms: (state, getters) => (perms) => {
      /*
        Possède la permission view_devis:
        permissions: [["view_devis"]]
        OU
        permissions: ["view_devis"]

        Possède la permission view_devis ET view_liste
        permissions: [["view_devis", "view_liste"]]
        OU
        permissions: ["view_devis", "view_liste"]

        Possède la permission view_devis OU view_liste
        permissions: [["view_devis"], ["view_liste"]]

        Possède la permission view_devis OU (view_liste ET view_facture)
        permissions: [["view_devis"], ["view_liste", "view_facture"]]
      */
      if (!perms) {
        return true;
      }

      if (typeof perms[0] === "string" || perms[0] instanceof String) {
        // Tableau de strings = possède toutes les perms
        return perms.every((p) => ((p[0] === "!" ? !getters.hasPerm(p.slice(1)) : getters.hasPerm(p))));
      }
      // Tableau de tableaux = possède au moins toutes les perms d'un des tableaux enfants
      return perms.some((arrPerm) => arrPerm.every(
        (p) => (p[0] === "!" ? !getters.hasPerm(p.slice(1)) : getters.hasPerm(p)),
      ));
    },
    impersonator(state) {
      return state.user ? state.user.impersonator : null;
    },
    impersonating(state) {
      return !!(state.user && state.user.impersonator && state.user.impersonator.username);
    },
    askChooseOrg(state) {
      return state.askChooseOrg;
    },
    urlHotline(state) {
      let url = `${config.url.hotlineTicket}?categorie=33`; // https://hotline.lde.fr/scenarisation/viewer/

      const id = state.Organisme?.organismeActuel?.id_organisme;

      if (id) {
        url += `&id_client=${id}`;
      }

      return url;
    },
    urlCristalWeb(state, getters) {
      const urlBase = config.url.cristalWeb;

      if (getters.isGMBH) {
        return urlBase;
      }

      const numServer = state.Organisme?.organismeActuel?.infos?.num_serveur_cw;
      const dotIndex = urlBase.indexOf(".");

      // On ajoute le numéro du serveur dans l'URL
      return urlBase.slice(0, dotIndex) + numServer + urlBase.slice(dotIndex);
    },
    searchKey(state) {
      return state.componentSearchKey;
    },
    advancedSearchActive(state) {
      return state.advancedSearchActive;
    },
    selectedLines(state) {
      return state.selectedLines;
    },
    isGMBH() {
      return window.location.host.endsWith(".lde.de");
    },
    activeLists(state) {
      return state.activeLists;
    },
    isFetchingActiveLists(state) {
      return state.isFetchingActiveLists;
    },
    panierToListResolver(state) {
      return state.panierToListResolver;
    },
    identifyFailed(state) {
      return state.identifyFailed;
    },
    sessionConflict(state) {
      return state.sessionConflict;
    },
    sessionExpiree(state) {
      return state.sessionExpiree;
    },
    avertissementSessionAffiche(state) {
      return state.finSessionProche
        && !state.sessionExpiree
        && state.avertissementIgnorePour !== state.echeanceSession?.expireLe;
    },
    taches(state) {
      return state.taches;
    },
    devisAjoutReference(state) {
      return state.devisAjoutReference;
    },
    modeAjoutReference(state) {
      return !!state.devisAjoutReference?.id;
    },
    referencesAjouteesAuDevis(state) {
      return state.referencesAjouteesAuDevis;
    },
    nouveauPrixDevisModif(state) {
      let prixHt = parseFloat(state.devisAjoutReference.total_ht);
      let prixTtc = parseFloat(state.devisAjoutReference.total_ttc);

      state.referencesAjouteesAuDevis.forEach((ref) => {
        prixHt += parseFloat(ref.offre.prix_ht) * ref.quantite;
        prixTtc += parseFloat(ref.offre.prix_editeur) * ref.quantite;
      });

      return { ht: prixHt, ttc: prixTtc };
    },
    modificationsDevis(state) {
      return state.modificationsDevis;
    },
    isMaitreComptaRegion(state) {
      return state.isMaitreComptaRegion;
    },
    parametres(state) {
      return state.parametres;
    },
  },
  mutations: {
    setLogged(state) {
      state.isLogged = true;
    },
    setSessionExpiree(state, bool) {
      state.sessionExpiree = bool;
      state.identiteExpiree = bool
        ? {
          username: state.user?.username,
          idOrganisme: state.Organisme?.organismeActuel?.id_organisme,
          // Clé attendue par /switch/ pour resélectionner l'organisme après reconnexion
          pkOrganisme: state.Organisme?.organismeActuel?.id,
        }
        : null;
    },
    setFinSessionProche(state, bool) {
      state.finSessionProche = bool;
    },
    ignorerAvertissementSession(state) {
      state.avertissementIgnorePour = state.echeanceSession?.expireLe;
    },
    setEcheanceSession(state, { expire_le: expireLe, maintenant }) {
      state.echeanceSession = expireLe
        ? { expireLe: Date.parse(expireLe), decalage: Date.parse(maintenant) - Date.now() }
        : null;
    },
    setUser(state, user) {
      state.user = user;
      if (user?.avatar) {
        state.user.avatar = user.avatar;
      }
    },
    setParametres(state, parametres) {
      state.parametres = parametres;
    },
    setAvatar(state, { avatar, avatar_from_library: lib }) {
      state.user.avatar = avatar;
      state.user.avatar_from_library = lib;
    },
    setPermissions(state, permissions) {
      state.permissions = permissions;
    },
    setDevisCount(state, devisCount) {
      state.devisCount = devisCount;
    },
    setDevisSoumisApprouvesCount(state, devisSoumisApprouvesCount) {
      state.devisSoumisApprouvesCount = devisSoumisApprouvesCount;
    },
    setAccessForbidden(state, accessForbidden) {
      state.accessForbidden = accessForbidden;
    },
    setPeutIgnorerBlocage(state, peutIgnorerBlocage) {
      state.peutIgnorerBlocage = peutIgnorerBlocage;
    },
    setAskLinkAccount(state, askLinkAccount) {
      state.askLinkAccount = askLinkAccount;
    },
    setErrorStatus(state, errorStatus) {
      state.errorStatus = errorStatus;
    },
    setCookies(state, cookies) {
      if (!state.isLogged) {
        if (cookies && cookies.date) {
          localStorage.setItem("cookies_consent", JSON.stringify(cookies));
        } else {
          localStorage.removeItem("cookies_consent");
        }
      }
      state.user.cookies = cookies;
    },
    setIsSuggestionFilterOpen(state, isSuggestionFilterOpen) {
      // Si elle n'existe pas, on met la valeur true, sinon on met la valeur récupérer de String en Boolean
      const isOpen = isSuggestionFilterOpen === null || JSON.parse(isSuggestionFilterOpen);
      state.isSuggestionFilterOpen = isOpen;
      localStorage.setItem("isSuggestionFilterOpen", String(isOpen));
    },
    setSearchSuggestionFilter(state, suggestionFilter) {
      state.suggestionFilter = suggestionFilter || null;
      if (suggestionFilter) {
        localStorage.setItem("suggestionFilter", suggestionFilter);
      }
    },
    updatePreferences(state, preferences) {
      state.preferences = { ...state.preferences, ...preferences };
      localStorage.setItem("preferences", JSON.stringify(state.preferences));
    },
    setAskChooseOrg(state, askChooseOrg) {
      state.askChooseOrg = askChooseOrg;
    },
    addSearchKey(state) {
      state.componentSearchKey += 1;
    },
    toggleAdvSearch(state) {
      state.advancedSearchActive = !state.advancedSearchActive;
    },
    setSelectedLines(state, lines) {
      state.selectedLines = lines;
    },
    setActiveLists(state, activeLists) {
      state.activeLists = activeLists;
    },
    setIsFetchingActiveLists(state, bool) {
      state.isFetchingActiveLists = bool;
    },
    setPanierToListResolver(state, resolver) {
      state.panierToListResolver = resolver;
    },
    setIdentifyFailed(state, bool) {
      state.identifyFailed = bool;
    },
    setSessionConflict(state, bool) {
      state.sessionConflict = bool;
    },
    setTaches(state, taches) {
      state.taches = taches;
    },
    setDevisAjoutReference(state, devis) {
      state.devisAjoutReference = devis;
    },
    setModificationsDevis(state, modifications) {
      state.modificationsDevis = modifications;
    },
    setIsMaitreComptaRegion(state, isMCR) {
      state.isMaitreComptaRegion = isMCR;
    },
    setCataloguePublic(state, cataloguePublic) {
      state.cataloguePublic = cataloguePublic;
    },
    setCataloguePublicActif(state, bool) {
      state.cataloguePublicActif = bool;
    },
    setCataloguePublicPays(state, pays) {
      state.cataloguePublicPays = pays;
    },
    setIsRedirecting(state, bool) {
      state.isRedirecting = bool;
    },
    setIsInitialCheckDone(state, bool) {
      state.isInitialCheckDone = bool;
    },
  },
  actions: {
    /**
     * Vérifie, après une expiration, si l'utilisateur s'est reconnecté (dans un autre onglet).
     * N'utilise pas l'action login : un retour anonyme écraserait l'état connecté de la page
     * et une autre identité ne doit jamais être chargée dans cette page.
     * Un utilisateur multi-organisme revient sans organisme choisi : on resélectionne le précédent.
     * Un autre organisme déjà choisi est un autre contexte et compte comme un autre utilisateur.
     * @returns {Promise<String>} "meme_utilisateur", "autre_utilisateur" ou "toujours_expiree".
     */
    verifierRetourSession(store) {
      return Api({ noHeader: true })
        .post("/identify/", { noRedirect: true })
        .then(async ({ data }) => {
          if (!data.user) {
            return "toujours_expiree";
          }
          const identite = store.state.identiteExpiree;
          if (data.user.username !== identite?.username) {
            return "autre_utilisateur";
          }
          if (!data.organisme_actuel && identite.pkOrganisme) {
            const reprise = await reselectionnerOrganisme(identite);
            if (reprise === "refus") {
              return "autre_utilisateur";
            }
            if (reprise === "echec") {
              // Panne passagère : recharger ferait perdre le travail, la modale reste et la prochaine
              // vérification (retour sur l'onglet, nouvelle connexion) retentera
              return "toujours_expiree";
            }
          } else if (data.organisme_actuel?.id_organisme !== identite.idOrganisme) {
            return "autre_utilisateur";
          }
          store.commit("setSessionExpiree", false);
          store.dispatch("rafraichirEcheanceSession");
          return "meme_utilisateur";
        })
        .catch(() => "toujours_expiree");
    },
    /**
     * Relit l'échéance de la session. À appeler après chaque identify ou prolongation :
     * identify renouvelle la session des utilisateurs mono-organisme.
     */
    rafraichirEcheanceSession(store) {
      return Api()
        .get("/session/")
        .then(({ data }) => store.commit("setEcheanceSession", data))
        .catch(() => store.commit("setEcheanceSession", {}));
    },
    /**
     * Contrôle la session sans attendre une action de l'utilisateur (retour sur l'onglet, échéance atteinte),
     * pour qu'une session terminée soit signalée avant qu'il ne recommence à saisir.
     * Une autre identité dans le cookie compte comme une fin de session : la modale gère ce cas.
     * @param {Object} options
     * @param {Boolean} options.expireeSiInjoignable Échéance atteinte : sans réponse du serveur, la session est
     * tenue pour expirée, sinon l'avertissement resterait figé à 0:00.
     */
    verifierEtatSession(store, { expireeSiInjoignable = false } = {}) {
      if (!store.state.isLogged || store.state.sessionExpiree) {
        return Promise.resolve();
      }
      return Api()
        .get("/session/")
        .then(({ data }) => {
          if (!data.authentifie || data.username !== store.state.user?.username) {
            store.commit("setSessionExpiree", true);
          } else {
            store.commit("setEcheanceSession", data);
          }
        })
        // Réseau indisponible : hors échéance, rien à conclure, la prochaine action tranchera
        .catch(() => {
          if (expireeSiInjoignable) {
            store.commit("setSessionExpiree", true);
          }
        });
    },
    /**
     * Prolonge la session sur demande explicite de l'utilisateur (« Rester connecté »).
     */
    prolongerSession(store) {
      return Api()
        .post("/session/prolonger/", {}, { sansMiseEnAttente: true })
        .then(({ data }) => store.commit("setEcheanceSession", data));
    },
    login(store, body) {
      // On ne mutualise que les vérifications passives : un appel actif (ex. chosen_consulting)
      // porte un body qui lui est propre et doit toujours atteindre le backend
      if (body?.noRedirect && inFlightLoginPromise) {
        return inFlightLoginPromise;
      }
      Sentry.init();
      Sentry.setScope();

      const request = Api({ noHeader: true })
        .post("/identify/", body)
        .then(async ({ data }) => {
          store.commit("setIsInitialCheckDone", true);
          // Non authentifié : on stocke les permissions publiques et le slug du catalogue
          if (!data.user) {
            store.commit("setPermissions", data.permissions || []);
            store.commit("setParametres", data.parametres);
            store.commit("setCataloguePublic", data.catalogue_public || null);
            store.commit("setCataloguePublicActif", Boolean(data.catalogue_public_actif));
            store.commit("setCataloguePublicPays", data.pays_catalogue_public || null);
            store.dispatch("setPanier");

            // Apply cookie preferences for guest users
            const guestCookies = store.state.user?.cookies;
            if (guestCookies) {
              if (guestCookies.audience && window._paq) {
                window._paq.push(["setConsentGiven"]);
              }
              if (guestCookies.date) {
                const today = Moment();
                const dateCookie = Moment(guestCookies.date, "DD/MM/YYYY - HH:mm:ss");
                if (dateCookie.isValid() && dateCookie.diff(today, "months", true) > 6) {
                  store.commit("setCookies", {});
                }
              }
            }
            return data;
          }

          store.commit("setUser", data.user);
          if (window._paq) {
            window._paq.push(["setUserId", data.user.id]);
          }

          if (store.state.user?.cookies?.audience && window._paq) {
            window._paq.push(["setConsentGiven"]);
          }

          const today = Moment();
          const dateCookie = Moment(store.state.user?.cookies?.date, "DD/MM/YYYY - HH:mm:ss");
          // Si le cookie est supérieur à 6 mois
          if (dateCookie.diff(today, "months", true) > 6) {
            store.commit("setCookies", {});
          }

          store.commit("setOrganismeActuel", data.organisme_actuel);
          store.commit("setPermissions", data.permissions);
          store.commit("setParametres", data.parametres);
          store.commit("setDevisCount", data.devis_count);
          store.commit("setDevisSoumisApprouvesCount", data.devis_soumis_approuves_count);
          store.commit("setAskLinkAccount", data.ask_link_account);
          store.commit("setAskChooseOrg", data.ask_choose_org);
          store.commit("setAccessForbidden", data.forbidden);
          store.commit("setPeutIgnorerBlocage", Boolean(data.peut_ignorer_blocage));

          if (localStorage) {
            store.commit("setSearchSuggestionFilter", localStorage.getItem("suggestionFilter"));
            store.commit("setIsSuggestionFilterOpen", localStorage.getItem("isSuggestionFilterOpen"));
          }

          store.commit("setLogged");
          store.dispatch("rafraichirEcheanceSession");
          // Notifie les autres onglets (même origine) qu'une connexion a eu lieu
          if (localStorage) {
            localStorage.setItem("session_event", String(Date.now()));
          }

          if (data.organisme_actuel) {
            store.dispatch("setPanier");
            store.dispatch("fetchActiveLists");
          }

          store.commit("setIsMaitreComptaRegion", data.permissions.includes("can_view_as_maitre_compta"));

          return data;
        })
        .catch((err) => {
          store.commit("setIsInitialCheckDone", true);
          store.dispatch("setPanier");
          if (body && body.noRedirect) {
            return false;
          }
          if (err.response && err.response.status === 401) {
            window.location = `${config.loginUrl}?next=${window.location.href}`;
          } else if (err.response
            && err.response.status === 403
            && err.response.data.detail.indexOf("CSRF") > -1
          ) {
            document.cookie.split(";").forEach((c) => {
              document.cookie = c.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
            });
            window.location = config.logoutUrl;
          } else {
            store.commit("setPermissions", [
              // Catalogue public
              "view_manuelnumerique",
            ]);
            const infos = {
              name: "IDENTIFY_FAILED",
              date: new Date(),
              user: store.state?.user?.username,
              error: err.message,
              store: store.state,
              localStorage: localStorage.valueOf(),
              cookies: document.cookie,
            };

            class IdentifyFailed extends Error {
              constructor() {
                super("Identify failed");
                this.name = "Identify failed";
              }
            }

            Sentry.client.captureException(new IdentifyFailed(), {
              extra: infos,
            });
            console.error(err);
            store.commit("setIdentifyFailed", true);
          }

          return true;
        })
        .finally(() => {
          store.commit("setIsInitialCheckDone", true);
          // Un appel plus récent a pu prendre la place : on ne libère que la sienne
          if (inFlightLoginPromise === request) {
            inFlightLoginPromise = null;
          }
        });

      inFlightLoginPromise = request;
      return request;
    },
    /**
     * Enregistre le pays choisi par le visiteur et met à jour le catalogue public associé.
     * @param {Object} store
     * @param {String} pays Code du pays (france, suisse, belgique ou autre).
     * @returns {Promise} Réponse de l'API.
     */
    setPaysCataloguePublic(store, pays) {
      return Api().post("/catalogue_public/pays/", { pays })
        .then(({ data }) => {
          store.commit("setCataloguePublic", data.catalogue_public || null);
          store.commit("setCataloguePublicPays", data.pays_catalogue_public || null);
          if (data.panier_vide) {
            store.dispatch("setPanier");
          }
          return data;
        });
    },
    toggleAdvSearch(store, toggle) {
      store.commit("toggleAdvSearch", toggle);
    },
    /**
     * Récupère les listes actives de l'utilisateur pour le site en général en dehors des vues "liste".
     * @param {Object} commit
     * @param {Object} getters
     * @returns {Promise} Listes actives.
     */
    fetchActiveLists({ commit }) {
      commit("setIsFetchingActiveLists", true);
      const params = {
        date_transfert__isnull: true,
        panier: false,
      };

      return Api().get("/liste/", { params })
        .then(({ data }) => {
          commit("setActiveLists", data.results);
          commit("setIsFetchingActiveLists", false);
          return data;
        });
    },
    fetchTaches({ commit }) {
      const repo = new TacheRepository();
      return repo.recupererTout().then((taches) => {
        commit("setTaches", taches);
      }).catch((error) => {
        commit("setTaches", []);
        console.error("Erreur lors de la récupération des tâches");
        throw error;
      });
    },
    desactiverAjoutReferenceSurDevis(store) {
      store.commit("setDevisAjoutReference", null);
      store.commit("setModificationsDevis", []);
      store.state.referencesAjouteesAuDevis = [];
    },
    activerAjoutReferenceSurDevis(store, devis) {
      store.commit("setDevisAjoutReference", devis);
      // Import dynamique : le routeur importe déjà le store (dépendance circulaire)
      import("@/router").then(({ default: router }) => router.push({ name: "home" }));
    },
    ajouterReferenceAuDevis({ state }, data) {
      state.referencesAjouteesAuDevis.push(data);
      useToast().success({
        title: i18n.global.t("devis.offre-temporairement-ajoutee"),
        delay: 10000,
      });
    },
    supprimerReferenceAuDevis({ state }, offreId) {
      state.referencesAjouteesAuDevis = state.referencesAjouteesAuDevis.filter(
        (ref) => ref.offre.id !== offreId,
      );
    },
  },
});
